// =========================================================================
// RouteCards.transform — pure projections behind the route cards, the cause
// pill and the "sent" bookkeeping. Issues module, fork-owned (zone C).
//
// A `RoutingDecision` from the reliability engine carries a flat list of
// `routes` (one per person) and a flat list of `actions` (one per ask, each
// owned by a role). The card wants them joined — one card per person, with
// the asks that person owns — and grouped by which side of the house acts:
// Operations (supervisor, superintendent, mine control) or Maintenance
// (reliability engineer, planner). That split is the reviewer's point.
//
// Transformation layer: pure functions — no React, no engine mutations.
//
// @see docs/logic/issues/alert-routing.md
// =========================================================================

import type { MineCode } from '@/versions/v1/schema/machine';
import type {
  RootCause,
  RouteChannel,
  RoutingAction,
  RoutingDecision,
  SiteRole,
} from '@/versions/v1/schema/operations';
import { ROLE_SIDE } from '@/versions/v1/shared/seed/site-roles';

// ---------------------------------------------------------------------------
// Cause
// ---------------------------------------------------------------------------

/** Display for one root cause. Colours are categorical series tokens, not status. */
export interface CauseMeta {
  /** Long label, e.g. "Operator behaviour". */
  readonly label: string;
  /** Short label for dense rows, e.g. "Operator". */
  readonly short: string;
  readonly color: string;
  readonly soft: string;
  /** One line: what the call means and who acts. */
  readonly means: string;
}

export const CAUSE_META: Readonly<Record<RootCause, CauseMeta>> = {
  operator: {
    label: 'Operator behaviour',
    short: 'Operator',
    color: 'var(--info)',
    soft: 'var(--info-soft)',
    means: 'How the machine is driven — goes to the operating supervisor, superintendent and mine control.',
  },
  equipment: {
    label: 'Equipment condition',
    short: 'Equipment',
    color: 'var(--warn)',
    soft: 'var(--warn-soft)',
    means: 'The component itself is going — goes to the reliability engineer and the maintenance planner.',
  },
  mixed: {
    label: 'Mixed — operator and equipment',
    short: 'Mixed',
    color: 'var(--band-major)',
    soft: 'var(--band-major-soft)',
    means: 'Worn component and a driver making it worse — both sides act, each on their half.',
  },
  insufficient: {
    label: 'Insufficient evidence',
    short: 'Unclear',
    color: 'var(--ink-tertiary)',
    soft: 'var(--surface-3)',
    means: 'Too little signal to call it — a reliability look before anyone acts.',
  },
};

/** Order causes are listed in filters and KPIs. */
export const CAUSE_ORDER: readonly RootCause[] = ['operator', 'equipment', 'mixed', 'insufficient'];

/** `0.89` → `"89%"`. */
export const pct = (n: number): string => `${Math.round(n * 100)}%`;

// ---------------------------------------------------------------------------
// Route cards
// ---------------------------------------------------------------------------

export type RouteSide = 'operations' | 'maintenance';

/** The one button on a card, and what pressing it says. */
export interface RoutePrimary {
  readonly label: string;
  /** Replaces the label once pressed. */
  readonly doneLabel: string;
  readonly toast: string;
}

/** One person a decision is pushed to, with the asks they own. */
export interface RouteCardModel {
  /** Stable within a decision — the role. */
  readonly key: string;
  readonly side: RouteSide;
  readonly role: SiteRole;
  readonly personName: string;
  readonly initials: string;
  readonly mine: MineCode;
  readonly channel: RouteChannel;
  readonly why: string;
  readonly synthesised: boolean;
  readonly actions: readonly RoutingAction[];
  /** The part a planner card schedules, when there is one. */
  readonly part: {
    readonly partNo: string;
    readonly siteStock: number | null;
    readonly leadTimeDays: number | null;
  } | null;
  readonly primary: RoutePrimary;
}

export interface RouteGroupModel {
  readonly side: RouteSide;
  readonly label: string;
  readonly blurb: string;
  readonly cards: readonly RouteCardModel[];
}

const SIDE_COPY: Readonly<Record<RouteSide, { label: string; blurb: string }>> = {
  operations: {
    label: 'Operations',
    blurb: 'Supervisor · superintendent · mine control — how it is driven',
  },
  maintenance: {
    label: 'Maintenance',
    blurb: 'Reliability engineer · planner — what is wearing, and the part',
  },
};

/** `"Brodie Keane"` → `"BK"`. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * The card's one button. Keyed by channel first (a superintendent on a
 * critical alert restricts; otherwise they take the handover), then role.
 */
export function primaryFor(
  role: SiteRole,
  channel: RouteChannel,
  decision: Pick<RoutingDecision, 'machine' | 'component' | 'mine'>,
  personName: string,
  partNo: string | null,
  partDescription: string | null,
): RoutePrimary {
  const who = `${personName} (${role}, ${decision.mine})`;
  const comp = decision.component.toLowerCase();
  switch (channel) {
    case 'Coaching session':
    case 'Pre-start brief':
      return {
        label: 'Send brief',
        doneLabel: 'Brief sent',
        toast: `Coaching brief on ${decision.machine} ${comp} sent to ${who}`,
      };
    case 'FMS restriction':
      return {
        label: 'Push restriction',
        doneLabel: 'Restriction pushed',
        toast: `${decision.machine} restriction sent to ${who} for sign-off in the FMS`,
      };
    case 'Shift handover':
      return {
        label: 'Add to handover',
        doneLabel: 'On handover',
        toast: `${decision.machine} added to the shift handover for ${who}`,
      };
    case 'RCA / FMEA review':
      return {
        label: 'Open RCA',
        doneLabel: 'RCA opened',
        toast: `RCA on ${decision.machine} ${comp} opened and assigned to ${who}`,
      };
    case 'Parts reservation': {
      const pads = /pad/i.test(partDescription ?? '');
      return {
        label: pads ? 'Schedule pads' : 'Reserve part',
        doneLabel: pads ? 'Pads scheduled' : 'Part reserved',
        toast: `${partNo ?? 'Part'} reservation for ${decision.machine} sent to ${who} to schedule`,
      };
    }
    case 'Work order':
      return {
        label: 'Raise work order',
        doneLabel: 'Work order raised',
        toast: `Inspection work order on ${decision.machine} raised for ${who}`,
      };
    case 'Triage queue':
    default:
      return {
        label: 'Add to triage',
        doneLabel: 'In triage',
        toast: `${decision.machine} ${comp} added to the reliability triage queue for ${who}`,
      };
  }
}

/** Pulls the part description back out of the engine's planner `why` line. */
function partDescriptionOf(why: string): string | null {
  const m = /\(([^)]+)\)/.exec(why);
  return m ? m[1] : null;
}

/**
 * One card per route, carrying the actions its role owns, grouped
 * Operations then Maintenance. Empty groups are dropped.
 *
 * Invariant: every route in the decision appears on exactly one card, in the
 * engine's order within its group.
 */
export function routeGroups(decision: RoutingDecision): RouteGroupModel[] {
  const cards: RouteCardModel[] = decision.routes.map((r) => {
    const actions = decision.actions.filter((a) => a.owner === r.role);
    const partAction = actions.find((a) => a.kind === 'schedule-parts');
    const partDescription = r.channel === 'Parts reservation' ? partDescriptionOf(r.why) : null;
    return {
      key: r.role,
      side: ROLE_SIDE[r.role],
      role: r.role,
      personName: r.personName,
      initials: initialsOf(r.personName),
      mine: r.mine,
      channel: r.channel,
      why: r.why,
      synthesised: r.synthesised,
      actions,
      part: partAction?.partNo
        ? {
            partNo: partAction.partNo,
            siteStock: partAction.siteStock ?? null,
            leadTimeDays: partAction.leadTimeDays ?? null,
          }
        : null,
      primary: primaryFor(r.role, r.channel, decision, r.personName, partAction?.partNo ?? null, partDescription),
    };
  });
  return (['operations', 'maintenance'] as const)
    .map((side) => ({ side, ...SIDE_COPY[side], cards: cards.filter((c) => c.side === side) }))
    .filter((g) => g.cards.length > 0);
}

/** Stock line for a planner card: "3 on the MI shelf" or "none at MI · 12-day lead". */
export function stockLine(part: NonNullable<RouteCardModel['part']>, mine: MineCode): string {
  if (part.siteStock === null) return part.leadTimeDays === null ? '' : `${part.leadTimeDays}-day lead`;
  if (part.siteStock > 0) {
    return `${part.siteStock} on the ${mine} shelf${part.leadTimeDays === null ? '' : ` · ${part.leadTimeDays}-day lead to restock`}`;
  }
  return `none at ${mine}${part.leadTimeDays === null ? '' : ` · ${part.leadTimeDays}-day lead`}`;
}

// ---------------------------------------------------------------------------
// Sent state
// ---------------------------------------------------------------------------

/** Identifies a decision across renders: the alert, or the machine × component. */
export function decisionKey(d: Pick<RoutingDecision, 'alertId' | 'machine' | 'component'>): string {
  return d.alertId ?? `${d.machine}|${d.component}`;
}

/** Identifies one card's send within a decision. */
export const sentKey = (decision: string, cardKey: string): string => `${decision}::${cardKey}`;

// ---------------------------------------------------------------------------
// Alert causes — the pill on each alert card and the Issues Live link card
// ---------------------------------------------------------------------------

export interface AlertCause {
  readonly cause: RootCause;
  readonly confidence: number;
}

/** "1 mixed · 6 equipment" over the open alerts, in display order, zeros dropped. */
export function causeSummary(causes: Iterable<AlertCause>): string {
  const n: Record<RootCause, number> = { operator: 0, equipment: 0, mixed: 0, insufficient: 0 };
  for (const c of causes) n[c.cause] += 1;
  return (['operator', 'mixed', 'equipment', 'insufficient'] as const)
    .filter((c) => n[c] > 0)
    .map((c) => `${n[c]} ${CAUSE_META[c].short.toLowerCase()}`)
    .join(' · ');
}

/** How many routes, split by side — "3 operations · 2 maintenance". */
export function sideCounts(decision: Pick<RoutingDecision, 'routes'>): Record<RouteSide, number> {
  const out: Record<RouteSide, number> = { operations: 0, maintenance: 0 };
  for (const r of decision.routes) out[ROLE_SIDE[r.role]] += 1;
  return out;
}
